import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  TablePagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { notaEntradaService, NotaEntrada } from '../../services/notaEntrada';

const NotaEntradaTable: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const { data: entradas = [], isLoading, error } = useQuery({
    queryKey: ['notasEntrada'],
    queryFn: notaEntradaService.getAll,
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const handleCadastro = () => {
    navigate('/entradas/novo');
  };

  const handleVisualizar = (id: number) => {
    navigate(`/entradas/visualizar/${id}`);
  };

  const filteredEntradas = entradas.filter(entrada =>
    entrada.fornecedor.toLowerCase().includes(search.toLowerCase()) ||
    entrada.id.toString().includes(search)
  );

  // Paginação dos dados filtrados
  const paginatedEntradas = filteredEntradas.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (error) {
    return (
      <Card>
        <CardContent>
          <Typography color="error" sx={{ textAlign: 'center', py: 4 }}>
            Erro ao carregar notas de entrada. Por favor, recarregue a página.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent sx={{ pa: 0 }}>
        {/* Toolbar */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <TextField
            size="small"
            placeholder="Filtrar"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }
            }}
            sx={{ minWidth: 300 }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCadastro}
            sx={{ backgroundColor: 'primary.main', '&:hover': { backgroundColor: 'primary.dark' } }}
          >
            Nova Entrada
          </Button>
        </Box>

        {/* Tabela */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Fornecedor</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Data de Compra</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Data de Inclusão</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Valor</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography sx={{ py: 4, color: 'text.secondary' }}>
                      Carregando...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedEntradas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography sx={{ py: 4, color: 'text.secondary' }}>
                      {search ? 'Nenhuma nota de entrada encontrada para o filtro informado.' : 'Nenhuma nota de entrada cadastrada.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedEntradas.map((entrada) => (
                  <TableRow key={entrada.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      #{entrada.id}
                    </TableCell>
                    <TableCell>{entrada.fornecedor}</TableCell>
                    <TableCell>{entrada.dataCompraString}</TableCell>
                    <TableCell>{entrada.dataInclusaoString}</TableCell>
                    <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>
                      {formatCurrency(entrada.valor)}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>
                      <IconButton
                        size="small"
                        onClick={() => handleVisualizar(entrada.id)}
                        color="primary"
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Paginação */}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredEntradas.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} de ${count} itens`
          }
          sx={{ 
            borderTop: '1px solid',
            borderColor: 'divider',
            '.MuiTablePagination-toolbar': {
              pl: 2,
              pr: 2
            }
          }}
        />
      </CardContent>
    </Card>
  );
};

export default NotaEntradaTable;
